import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../lib/api/client';
import { Button } from '../ui/Button';

export const CallAuditParamsUploadPage: React.FC = () => {
  const { user } = useAuth();
  const [orgId, setOrgId] = useState<string>('');
  const [teamLeadId, setTeamLeadId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<any | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (user) {
      setTeamLeadId(user.id);
      if ((user as any).org_id) setOrgId((user as any).org_id);
    }
  }, [user]);

  const fetchConfig = async () => {
    if (!orgId || !teamLeadId) return;
    try {
      setLoading(true);
      const data = await apiClient.getCallAuditParams(orgId, teamLeadId);
      setConfig(data);
    } catch (e: any) {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [orgId, teamLeadId]);

  const onUpload = async () => {
    if (!file || !orgId || !teamLeadId) return;
    try {
      setError(null);
      setMessage(null);
      setLoading(true);
      const res = await apiClient.uploadCallAuditParams(orgId, teamLeadId, file);
      if (res?.status === 'validation_failed') {
        setError(`Validation failed: ${(res.errors || []).join('; ')}`);
      } else {
        setMessage('Parameters uploaded successfully');
        await fetchConfig();
      }
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const onDownloadTemplate = async () => {
    try {
      setError(null);
      const blob = await apiClient.downloadCallAuditParamsTemplateCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'call_audit_params_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError('Failed to download template');
    }
  };

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-xl font-semibold">Call Audit Parameters</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm mb-1">Org ID</label>
          <input
            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white/60 dark:bg-black/30"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            placeholder="ORG ID"
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Team Lead ID</label>
          <input
            className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white/60 dark:bg-black/30"
            value={teamLeadId}
            onChange={(e) => setTeamLeadId(e.target.value)}
            placeholder="TEAM LEAD ID"
          />
        </div>
        <div>
          <label className="block text-sm mb-1">CSV/Excel File</label>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button disabled={loading || !file || !orgId || !teamLeadId} onClick={onUpload}>
          {loading ? 'Uploading...' : 'Upload'}
        </Button>
        <Button variant="secondary" onClick={fetchConfig}>Refresh</Button>
        <Button variant="ghost" onClick={onDownloadTemplate}>Download sample CSV</Button>
        <span className="text-xs text-text-enterprise-secondary self-center">Use this template to prepare your parameters. Category weight is auto-computed from parameter weights.</span>
      </div>

      {message && <div className="text-green-600 text-sm">{message}</div>}
      {error && <div className="text-red-600 text-sm">{error}</div>}

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-medium">Current Configuration</h3>
          {config && (
            <div className="flex gap-2">
              <Button variant={editMode ? 'secondary' : 'default'} onClick={() => setEditMode(!editMode)}>{editMode ? 'View' : 'Edit'}</Button>
              <Button variant="ghost" onClick={async ()=>{ if (!orgId||!teamLeadId) return; await apiClient.deleteCallAuditParams(orgId, teamLeadId); setConfig(null); }}>Delete</Button>
            </div>
          )}
        </div>
        {!config && <div className="text-sm text-gray-500">No configuration found.</div>}
        {config && !editMode && (
          <div className="overflow-y-auto rounded-xl border border-glass-border/40 bg-glass-surface/40">
            <table className="w-full table-fixed text-sm text-text-enterprise-primary/90">
              <colgroup>
                <col style={{ width: '12%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead className="bg-glass-surface/60 text-text-enterprise-secondary">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Category</th>
                  <th className="px-3 py-2 text-left font-medium">Cat. Weight</th>
                  <th className="px-3 py-2 text-left font-medium">Parameter</th>
                  <th className="px-3 py-2 text-left font-medium">Weight</th>
                  <th className="px-3 py-2 text-left font-medium">Mandatory</th>
                  <th className="px-3 py-2 text-left font-medium">What to consider</th>
                  <th className="px-3 py-2 text-left font-medium">Purpose</th>
                  <th className="px-3 py-2 text-left font-medium">Red flags</th>
                  <th className="px-3 py-2 text-left font-medium">Best practices</th>
                </tr>
              </thead>
              <tbody>
                {(config.parameter_categories || []).map((cat: any, cIdx: number) => {
                  const catWeight = typeof cat.category_weight === 'number'
                    ? cat.category_weight
                    : (cat.parameters || []).reduce((sum: number, p: any) => sum + (Number(p?.weight) || 0), 0);
                  return (cat.parameters || []).map((p: any, pIdx: number) => (
                    <tr key={`${cIdx}-${pIdx}-view`} className="border-t border-glass-border/30 align-top hover:bg-glass-surface/20 transition-colors">
                      <td className="px-3 py-2 whitespace-normal break-words">{cat.category_name || '—'}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{catWeight ?? '—'}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{p.parameter_name || '—'}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{p.weight ?? '—'}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{p.mandatory ? 'Yes' : 'No'}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{Array.isArray(p.what_to_consider) ? p.what_to_consider.join('; ') : (p.what_to_consider || '—')}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{p.purpose || '—'}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{Array.isArray(p.red_flags) ? p.red_flags.join('; ') : (p.red_flags || '—')}</td>
                      <td className="px-3 py-2 whitespace-normal break-words">{Array.isArray(p.best_practices) ? p.best_practices.join('; ') : (p.best_practices || '—')}</td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        )}
        {config && editMode && (
          <div className="overflow-auto rounded-xl border border-glass-border/40 bg-glass-surface/40">
            <table className="min-w-full text-sm text-text-enterprise-primary/90">
              <thead className="bg-glass-surface/60 text-text-enterprise-secondary">
                <tr>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Cat. Weight</th>
                  <th className="px-3 py-2 text-left">Parameter</th>
                  <th className="px-3 py-2 text-left">Weight</th>
                  <th className="px-3 py-2 text-left">Mandatory</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(config.parameter_categories || []).map((cat: any, cIdx: number) => (
                  (cat.parameters || []).map((p: any, pIdx: number) => (
                    <tr key={`${cIdx}-${pIdx}`} className="border-t border-glass-border/30">
                      <td className="px-3 py-2">
                        <input className="w-full px-2 py-1 border border-glass-border/40 bg-glass-surface/30 rounded text-text-enterprise-primary" value={cat.category_name}
                          onChange={(e)=>{
                            const cfg = {...config};
                            cfg.parameter_categories[cIdx].category_name = e.target.value;
                            setConfig(cfg);
                          }} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" className="w-24 px-2 py-1 border border-glass-border/40 bg-glass-surface/30 rounded text-text-enterprise-primary" value={cat.category_weight}
                          onChange={(e)=>{
                            const cfg = {...config};
                            cfg.parameter_categories[cIdx].category_weight = Number(e.target.value);
                            setConfig(cfg);
                          }} />
                      </td>
                      <td className="px-3 py-2">
                        <input className="w-full px-2 py-1 border border-glass-border/40 bg-glass-surface/30 rounded text-text-enterprise-primary" value={p.parameter_name}
                          onChange={(e)=>{
                            const cfg = {...config};
                            cfg.parameter_categories[cIdx].parameters[pIdx].parameter_name = e.target.value;
                            setConfig(cfg);
                          }} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" className="w-24 px-2 py-1 border border-glass-border/40 bg-glass-surface/30 rounded text-text-enterprise-primary" value={p.weight}
                          onChange={(e)=>{
                            const cfg = {...config};
                            cfg.parameter_categories[cIdx].parameters[pIdx].weight = Number(e.target.value);
                            setConfig(cfg);
                          }} />
                      </td>
                      <td className="px-3 py-2">
                        <input type="checkbox" className="accent-primary-500" checked={!!p.mandatory}
                          onChange={(e)=>{
                            const cfg = {...config};
                            cfg.parameter_categories[cIdx].parameters[pIdx].mandatory = e.target.checked;
                            setConfig(cfg);
                          }} />
                      </td>
                      <td className="px-3 py-2">
                        <Button variant="ghost" size="sm" onClick={()=>{
                          const cfg = {...config};
                          cfg.parameter_categories[cIdx].parameters.splice(pIdx,1);
                          setConfig(cfg);
                        }}>Remove</Button>
                      </td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2 p-3">
              <Button variant="secondary" onClick={()=>fetchConfig()}>Reset</Button>
              <Button onClick={async ()=>{
                if (!orgId||!teamLeadId) return;
                const body = {
                  configuration_name: config.configuration_name || 'Team Lead Call Audit Parameters',
                  description: config.description || 'Configured via UI',
                  parameter_categories: config.parameter_categories || [],
                  scoring_configuration: config.scoring_configuration || { method: 'weighted_average' },
                  report_preferences: config.report_preferences || { include_audio_clips: false },
                  applies_to_roles: config.applies_to_roles || ['recruiter']
                };
                const res = await apiClient.updateCallAuditParams(orgId, teamLeadId, body);
                if (res?.status === 'validation_failed') {
                  setError(`Validation failed: ${(res.errors || []).join('; ')}`);
                } else {
                  setMessage('Parameters saved');
                  setEditMode(false);
                  await fetchConfig();
                }
              }}>Save</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallAuditParamsUploadPage;
